// Satellite and spacecraft data
// Satellites orbit Earth, spacecraft travel through the solar system

export const satellites = [
  {
    name: 'ISS',
    type: 'Space Station',
    description: 'The International Space Station is a modular space station in low Earth orbit. A joint project of five space agencies.',
    orbitRadius: 4.5,
    orbitalSpeed: 0.04, // Scaled down for smoother motion
    inclination: 51.6,
    size: 0.12,
    color: 0xffffff,
    realSize: '109m x 73m',
    realAltitude: '~420 km'
  },
  // Flown on SGP4 from its latest elements (NORAD 20580), as the ISS is.
  // realAltitude: the CelesTrak elements of 30 September 2026 (mean motion
  // 15.3179 a day, a 6,848.6 km semi-major axis, 470 km over the equator;
  // JPL Horizons' TLE ephemeris gives 465 to 471 km that day). NASA's
  // 483 km is as of 1 March 2024; the orbit is decaying. Size: 13.2 m long,
  // 4.2 m across (NASA, Horizons' -48 record). docs/new-objects-verification.md.
  {
    name: 'Hubble',
    type: 'Space Telescope',
    description: 'NASA and ESA\'s space telescope, launched on 24 April 1990 and still at work about 470 km up.',
    norad: 20580,
    orbitRadius: 5,
    orbitalSpeed: 0.035, // Scaled down for smoother motion
    inclination: 28.5,
    size: 0.08,
    color: 0xffd700,
    realSize: '13.2 m x 4.2 m',
    realAltitude: '~470 km'
  },
  {
    name: 'Tiangong',
    type: 'Space Station',
    description: 'Tiangong is the Chinese modular space station in low Earth orbit.',
    orbitRadius: 4.3,
    orbitalSpeed: 0.042, // Scaled down for smoother motion
    inclination: 41.5,
    size: 0.1,
    color: 0xff6666,
    realSize: '110m',
    realAltitude: '390 km'
  },
  // Flown on JPL Horizons' path (-170, public/data/jwst-path.json) round
  // the Sun-Earth L2 point. realSize: the sunshield 21.197 m x 14.162 m and
  // the 6.5 m primary mirror (Horizons' -170 record, NASA's Webb pages);
  // until 3 November 2026 this said "21m mirror".
  {
    name: 'JWST',
    type: 'Space Telescope',
    description: 'The James Webb Space Telescope, in a halo orbit round the Sun-Earth L2 point about 1.5 million km from Earth.',
    orbitRadius: 15, // Representing L2 position scaled
    orbitalSpeed: 0.002, // Scaled down for smoother motion
    inclination: 5,
    size: 0.15,
    color: 0xffd700,
    realSize: '21.2 m x 14.2 m sunshield, 6.5 m mirror',
    realAltitude: '1.5M km (L2)'
  }
];

// Deep space probes, flown on their JPL Horizons paths (src/probes/
// trajectory.js, public/data/probe-trajectories.json). realDistance is the
// distance from the Sun on 10 October 2026, shown until the paths load; the
// viewer then keeps it live.
export const spaceProbes = [
  {
    name: 'Voyager 1',
    type: 'Space Probe',
    description: 'The most distant human-made object. Launched 1977, entered interstellar space 2012.',
    size: 0.3,
    color: 0x00ff88,
    realDistance: '25.73 billion km (172.0 AU)',
    launched: '1977'
  },
  {
    name: 'Voyager 2',
    type: 'Space Probe',
    description: 'Only spacecraft to visit all four giant planets. Entered interstellar space 2018.',
    size: 0.3,
    color: 0x00ff88,
    realDistance: '21.57 billion km (144.2 AU)',
    launched: '1977'
  },
  {
    name: 'New Horizons',
    type: 'Space Probe',
    description: 'First spacecraft to fly by Pluto (2015) and Kuiper Belt object Arrokoth (2019).',
    size: 0.25,
    color: 0x00aaff,
    realDistance: '9.84 billion km (65.8 AU)',
    launched: '2006'
  },
  {
    name: 'Pioneer 10',
    type: 'Space Probe',
    description: 'First spacecraft to travel through the asteroid belt and make direct observations of Jupiter.',
    size: 0.2,
    color: 0xffaa00,
    realDistance: '21.24 billion km (142.0 AU)',
    launched: '1972'
  }
];

// Near-Earth objects
export const nearEarthObjects = [
  {
    name: 'Apophis',
    type: 'Near-Earth Asteroid',
    description: 'Will pass about 32,000 km above the Earth\'s surface on 13 April 2029. Named after the Egyptian god of chaos.',
    orbitRadius: 155,
    eccentricity: 0.2,
    orbitalSpeed: 0.0012, // Scaled down for smoother motion
    inclination: 3.3,
    size: 0.2,
    color: 0x8b4513,
    realSize: '370 m'
  },
  {
    name: 'Bennu',
    type: 'Near-Earth Asteroid',
    description: 'Target of OSIRIS-REx mission. Samples returned to Earth in 2023.',
    orbitRadius: 165,
    eccentricity: 0.15,
    orbitalSpeed: 0.0014, // Scaled down for smoother motion
    inclination: 6,
    size: 0.15,
    color: 0x4a4a4a,
    realSize: '500 m'
  },
  {
    name: 'Eros',
    type: 'Near-Earth Asteroid',
    description: 'First asteroid orbited and landed upon (NEAR Shoemaker, 2001).',
    orbitRadius: 180,
    eccentricity: 0.25,
    orbitalSpeed: 0.0011, // Scaled down for smoother motion
    inclination: -4,
    size: 0.3,
    color: 0x9a7b4f,
    realSize: '16.8 km'
  },
  {
    name: 'Ryugu',
    type: 'Near-Earth Asteroid',
    description: 'Diamond-shaped asteroid visited by Hayabusa2. Samples returned 2020.',
    orbitRadius: 170,
    eccentricity: 0.18,
    orbitalSpeed: 0.0013, // Scaled down for smoother motion
    inclination: 5.9,
    size: 0.12,
    color: 0x3a3a3a,
    realSize: '900 m'
  }
];

// Comets
export const comets = [
  {
    name: "Halley's Comet",
    type: 'Periodic Comet',
    description: 'Most famous comet. Visible every 75-79 years. Last seen 1986, returns 2061.',
    perihelion: 70,
    aphelion: 850,
    orbitalSpeed: 0.0006, // Scaled down for smoother motion
    inclination: 18,
    startAngle: 0,
    size: 0.5,
    tailLength: 25,
    coreColor: 0x746e66,
    comaColor: 0xe5e8df,
    ionColor: 0x8ab5ff,
    dustColor: 0xe8dcc2,
    realSize: '11 km'
  },
  {
    name: 'Hale-Bopp',
    type: 'Long-period Comet',
    description: 'One of the most widely observed comets. Visible to naked eye for 18 months in 1997.',
    perihelion: 90,
    aphelion: 1400,
    orbitalSpeed: 0.0003, // Scaled down for smoother motion
    inclination: -45,
    startAngle: Math.PI * 0.7,
    size: 0.6,
    tailLength: 35,
    coreColor: 0x7e776f,
    comaColor: 0xe9eadf,
    ionColor: 0x9cc6ff,
    dustColor: 0xf0e2b7,
    realSize: '60 km'
  },
  {
    name: 'NEOWISE',
    type: 'Long-period Comet',
    description: 'Discovered 2020. Brightest comet in Northern Hemisphere for decades.',
    perihelion: 55,
    aphelion: 1600,
    orbitalSpeed: 0.0005, // Scaled down for smoother motion
    inclination: 65,
    startAngle: Math.PI * 1.3,
    size: 0.4,
    tailLength: 20,
    coreColor: 0x8c857b,
    comaColor: 0xeeeee5,
    ionColor: 0x8ebcff,
    dustColor: 0xffd6a3,
    realSize: '5 km'
  }
];

// The two interstellar visitors drawn in the viewer, on JPL Horizons' paths
// (public/data/interstellar-paths.json, scripts/build-visitor-paths.mjs) from
// 1800 to 2200. bodyId is the id of their info trees (src/data/bodies/
// smallbodies.js) and links (#oumuamua, #3i-atlas). size is the drawn radius
// in artistic units; 3I/ATLAS is drawn by the particle comet code, its
// perihelion in AU (getCometActivity reads its distance from the Sun in AU
// against it). Every figure is sourced in docs/new-objects-verification.md.
export const interstellarObjects = [
  {
    name: 'ʻOumuamua',
    bodyId: 'oumuamua',
    type: 'Interstellar Object',
    description: 'The first object known to have come from another star, found on 19 October 2017 as it left the inner solar system.',
    designation: '1I/2017 U1',
    size: 0.12,
    // Up to 400 m long and roughly ten times as long as it is wide, reddish
    // (NASA); its shape is not known, so the one drawn is an artistic
    // rendition of NASA's description.
    elongation: 10,
    color: 0x8a5d48,
    rotationHours: 7.3,
    realSize: 'Up to 400 m long'
  },
  {
    name: '3I/ATLAS',
    bodyId: '3i-atlas',
    type: 'Interstellar Comet',
    description: 'The third object known to have come from another star, a comet found by ATLAS on 1 July 2025.',
    designation: 'C/2025 N1',
    isComet: true,
    perihelion: 1.3564,
    size: 0.4,
    tailLength: 20,
    coreColor: 0x6f6a64,
    comaColor: 0xe1e6dc,
    ionColor: 0x8ab5ff,
    dustColor: 0xe9dcc0,
    realSize: '0.44 to 5.6 km nucleus'
  }
];

// Additional dwarf planets (beyond what's in celestialData.js). `elements`:
// JPL Horizons' osculating elements about the solar system's centre of mass
// (J2000 ecliptic, degrees, epoch JD 2461200.5 TDB as the belt bodies'
// ASTEROID_ELEMENTS_EPOCH_JD, n in degrees a day), which main.js places them
// from in both scales. A Kepler orbit on them stays within 0.05 AU of
// Horizons' own positions from 1800 to 2200 (2026 ED138, on a short arc,
// 0.2 AU in 1800); on the Sun-centred elements in the Small-Body Database,
// which Jupiter's pull on the Sun keeps changing, it strays by up to 1.7
// degrees (Makemake by 2200). Checked by tests/dwarf-planets.test.js.
export const dwarfPlanets = [
  {
    name: 'Ceres',
    type: 'Dwarf Planet',
    description: 'Largest object in asteroid belt. First asteroid discovered (1801). Has bright spots of salt deposits.',
    distance: 260,
    radius: 0.9,
    orbitalSpeed: 0.001, // Scaled down for smoother motion
    rotationSpeed: 0.003,
    rotationHours: 9.07, // real period, used in realistic scale
    tilt: 4,
    colors: ['#8a8a8a', '#5c5c5c'],
    realDiameter: '940 km'
  },
  {
    name: 'Eris',
    type: 'Dwarf Planet',
    elements: { a: 67.8351095, e: 0.43843102, i: 43.99297, peri: 151.22114, node: 35.97648, M: 210.8878996, n: 1.765279425e-03 },
    description: 'Most massive dwarf planet. Its discovery led to Pluto\'s reclassification.',
    radius: 1.0,
    rotationSpeed: 0.001,
    rotationHours: 378.9, // real period, used in realistic scale
    tilt: 44,
    // The map's mean colour, which stands in until it loads. Its surface is
    // an artistic rendition (scripts/textures/generate-dwarf-map.mjs,
    // docs/textures/dwarf-planets.md) and its panel says so.
    colors: ['#e2d6c4', '#b0b0b0'],
    texture: 'eris',
    realDiameter: '2,326 km'
  },
  {
    name: 'Makemake',
    type: 'Dwarf Planet',
    elements: { a: 45.4986328, e: 0.16038802, i: 29.00208, peri: 296.06534, node: 79.44145, M: 171.1395771, n: 3.213643776e-03 },
    description: 'Second-brightest Kuiper belt object. Extremely cold: -243°C.',
    radius: 0.75,
    rotationSpeed: 0.001,
    rotationHours: 22.83, // real period, used in realistic scale
    tilt: 29,
    colors: ['#cec2a8', '#8b6355'], // the map's mean (artistic rendition)
    texture: 'makemake',
    realDiameter: '1,430 km'
  },
  {
    name: 'Haumea',
    type: 'Dwarf Planet',
    elements: { a: 43.1023961, e: 0.19502602, i: 28.20483, peri: 239.94736, node: 121.94643, M: 224.0795121, n: 3.485323824e-03 },
    description: 'Elongated shape from rapid 4-hour rotation. Has two small moons.',
    radius: 0.65,
    rotationSpeed: 0.015, // Haumea spins fast but scaled down a bit
    rotationHours: 3.915, // real period, used in realistic scale
    tilt: 28,
    colors: ['#9fa1a1', '#909090'], // the map's mean (artistic rendition)
    texture: 'haumea',
    // Semi-axes in km, the shortest the spin axis (Ortiz et al. 2017,
    // Nature 550, 219): the mesh takes this shape at its volume.
    axesKm: [1161, 852, 513],
    realDiameter: '1,632 km'
  },
  {
    name: 'Sedna',
    type: 'Dwarf Planet Candidate',
    elements: { a: 506.4433929, e: 0.84955574, i: 11.92852, peri: 311.28476, node: 144.40158, M: 358.4295986, n: 8.653641328e-05 },
    description: 'One of the most distant objects known. Takes about 11,400 years to orbit the Sun.',
    radius: 0.5,
    rotationSpeed: 0.0005,
    rotationHours: 10.27, // real period, used in realistic scale
    tilt: 12,
    colors: ['#a08054', '#8b4444'], // the map's mean (artistic rendition)
    texture: 'sedna',
    realDiameter: '~1,000 km'
  },
  {
    // Found in Rubin Observatory data in October 2026 (MPEC 2026-U85). No
    // image resolves it, so its map is an artistic rendition
    // (scripts/textures/generate-dwarf-map.mjs ed138) and its panel says so.
    name: '2026 ED138',
    type: 'Dwarf Planet Candidate',
    elements: { a: 43.0911161, e: 0.13874015, i: 24.43389, peri: 168.70458, node: 322.60593, M: 127.5658672, n: 3.486692452e-03 },
    description: 'Rubin Observatory\'s first dwarf planet candidate, found in 2026 about 47 AU from the Sun. Its surface has never been seen, so the one shown is an artistic rendition.',
    radius: 0.5, // 600 to 1,500 km, about Sedna's size
    rotationSpeed: 0.001, // rotation not measured yet
    colors: ['#b8a294', '#8a6a5a'],
    texture: 'ed138',
    realDiameter: '600 to 1,500 km'
  }
];

// Named main-belt asteroids. These are the big, recognisable bodies
// that make the belt feel like a real population instead of noise.
// `elements`: JPL's osculating elements (ELEMENTS.NUMBR, heliocentric J2000
// ecliptic, degrees, epoch JD 2461200.5 TDB; NASA/JPL-Caltech), which place
// them in both scales (main.js) and as points of light (src/sky/asteroids.js;
// scripts/build-asteroids.mjs leaves these four out of the belt's file).
export const ASTEROID_ELEMENTS_EPOCH_JD = 2461200.5;
export const asteroidBeltBodies = [
  {
    name: 'Ceres',
    elements: { num: 1, a: 2.7655526, e: 0.07969230, i: 10.58803, peri: 73.29421, node: 80.24863, M: 274.4193464, H: 3.34, G: 0.12 },
    type: 'Dwarf Planet',
    description: 'Largest object in the asteroid belt. The only round body in the main belt and the first asteroid discovered.',
    distance: 260,
    radius: 0.9,
    orbitalSpeed: 0.001,
    inclination: 10.6,
    size: 0.9,
    color: 0x8a8a8a,
    realDiameter: '940 km'
  },
  {
    name: 'Vesta',
    elements: { num: 4, a: 2.3613660, e: 0.09020374, i: 7.14393, peri: 151.46865, node: 103.70129, M: 81.1901561, H: 3.25, G: 0.32 },
    type: 'Main-belt asteroid',
    description: 'Second most massive body in the asteroid belt. Differentiated crust and mantle, bright enough to be seen with binoculars at opposition.',
    distance: 250,
    radius: 0.56,
    orbitalSpeed: 0.00135,
    inclination: 7.1,
    size: 0.56,
    color: 0xb38c63,
    realDiameter: '525 km'
  },
  {
    name: 'Pallas',
    elements: { num: 2, a: 2.7695590, e: 0.23070010, i: 34.93279, peri: 310.96992, node: 172.88662, M: 254.2496522, H: 4.12, G: 0.11 },
    type: 'Main-belt asteroid',
    description: 'A highly inclined asteroid with one of the most tilted orbits in the main belt.',
    distance: 268,
    radius: 0.46,
    orbitalSpeed: 0.0012,
    inclination: 34.8,
    size: 0.46,
    color: 0xa8a8b8,
    realDiameter: '512 km'
  },
  {
    name: 'Hygiea',
    elements: { num: 10, a: 3.1509740, e: 0.10670927, i: 3.82953, peri: 312.42424, node: 283.11989, M: 252.0344242, H: 5.65, G: 0.15 },
    type: 'Main-belt asteroid',
    description: 'Fourth-largest asteroid in the belt. Dark, carbon-rich surface; nearly spherical despite its distance.',
    distance: 286,
    radius: 0.42,
    orbitalSpeed: 0.00105,
    inclination: 3.8,
    size: 0.42,
    color: 0x6f6f78,
    realDiameter: '434 km'
  }
];
